import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import Students from './components/Students';
import Campuses from './components/Campuses';
import Courses from './components/Courses';
import BulkImport from './components/BulkImport';
import Login from './components/Login';
import Hostels from './components/Hostels';
import HostelAdmissions from './components/HostelAdmissions';
import HostelFinances from './components/HostelFinances';
import HostelRequests from './components/HostelRequests';
import Attendance from './components/Attendance';
import InstituteAttendance from './components/InstituteAttendance';
import Meals from './components/Meals';
import HostelBilling from './components/HostelBilling';
import Invoices from './components/Invoices';
import Roles from './components/Roles';
import Users from './components/Users';
import AuditLog from './components/AuditLog';
import Notifications from './components/Notifications';
import HostelLogin from './components/hostel/HostelLogin';
import HostelDashboard from './components/hostel/HostelDashboard';
import HostelAdmissionsPortal from './components/hostel/HostelAdmissionsPortal';
import HostelFinancesPortal from './components/hostel/HostelFinancesPortal';
import HostelAttendancePortal from './components/hostel/HostelAttendancePortal';
import HostelMealsPortal from './components/hostel/HostelMealsPortal';
import HostelInvoicesPortal from './components/hostel/HostelInvoicesPortal';
import HostelAccountPortal from './components/hostel/HostelAccountPortal';
import HostelRequestsPortal from './components/hostel/HostelRequestsPortal';
import { verifyToken, logout, hostelVerifyToken, getUnreadNotificationCount } from './api';
import { PermissionsProvider, can } from './utils/permissions';
import {
  FiGrid, FiUsers, FiUpload, FiMapPin, FiBookOpen, FiCreditCard, FiClipboard,
  FiBriefcase, FiUserCheck, FiCheckSquare, FiCoffee, FiSliders, FiFileText,
  FiShield, FiActivity, FiLogOut, FiMenu, FiUser, FiBell, FiFlag
} from 'react-icons/fi';
import './index.css';

// ── Shared sidebar (used by both admin console and hostel portal) ──────
function Sidebar({ items, identity, onLogout, sidebarOpen, closeSidebar, badges = {} }) {
  const location = useLocation();
  return (
    <aside className="app-sidebar" id="app-sidebar">
      <Link to={items.homePath} className="sidebar-brand" onClick={closeSidebar}>
        <img src="/assets/hit-logo.png" alt="Hazza Institute of Technology" />
        <div className="sidebar-brand-text">
          <span>{identity.subtitle}</span>
        </div>
      </Link>

      <nav>
        {items.groups.map((group, gi) => (
          <div key={gi}>
            {group.label && <div className="sidebar-section-label">{group.label}</div>}
            {group.links.map(({ to, label, icon: Icon }) => {
              const active = location.pathname === to || location.pathname.startsWith(to + '/');
              const badgeCount = badges[to];
              return (
                <Link key={to} to={to} className={`nav-link ${active ? 'active' : ''}`} onClick={closeSidebar} style={{ justifyContent: 'space-between' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '11px' }}><Icon /> <span>{label}</span></span>
                  {!!badgeCount && (
                    <span style={{ background: active ? 'rgba(9,21,48,0.25)' : '#dc2626', color: '#fff', borderRadius: '999px', padding: '1px 7px', fontSize: '10.5px', fontWeight: 700 }}>
                      {badgeCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="sidebar-bottom">
        <div className="sidebar-user">
          <strong>{identity.name}</strong>
          {identity.sub}
        </div>
        <button className="sidebar-logout-btn" onClick={onLogout}>
          <FiLogOut /> Logout
        </button>
      </div>
    </aside>
  );
}

function MobileTopChrome({ toggleSidebar }) {
  return (
    <>
      <button className="app-sidebar-toggle" onClick={toggleSidebar} aria-label="Open menu">
        <FiMenu />
      </button>
    </>
  );
}

function useSidebarDrawer() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEffect(() => {
    document.body.classList.toggle('sidebar-open', sidebarOpen);
    return () => document.body.classList.remove('sidebar-open');
  }, [sidebarOpen]);
  const location = useLocation();
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);
  return [sidebarOpen, setSidebarOpen];
}

// ── Admin shell ──────────────────────────────────────────────────────
const ADMIN_NAV = {
  homePath: '/',
  groups: [
    {
      label: null,
      links: [
        { to: '/', label: 'Dashboard', icon: FiGrid, module: 'dashboard' },
        { to: '/notifications', label: 'Notifications', icon: FiBell, module: 'notifications' },
        { to: '/students', label: 'Students', icon: FiUsers, module: 'students' },
        { to: '/bulk-import', label: 'Bulk Import', icon: FiUpload, module: ['bulk_import_students', 'bulk_import_hostel'] },
        { to: '/campuses', label: 'Campuses', icon: FiMapPin, module: 'campuses' },
        { to: '/courses', label: 'Courses', icon: FiBookOpen, module: 'courses' },
      ],
    },
    {
      label: 'Hostel Management',
      links: [
        { to: '/hostels', label: 'Hostel Accounts', icon: FiBriefcase, module: 'hostels' },
        { to: '/hostel-admissions', label: 'Residents', icon: FiUserCheck, module: 'hostel_admissions' },
        { to: '/hostel-attendance', label: 'Attendance', icon: FiCheckSquare, module: 'hostel_attendance' },
        { to: '/institute-attendance', label: 'Institute Attendance', icon: FiFlag, module: 'institute_attendance' },
        { to: '/hostel-meals', label: 'Meals', icon: FiCoffee, module: 'hostel_meals' },
        { to: '/hostel-billing', label: 'Billing Config', icon: FiSliders, module: 'hostel_billing' },
        { to: '/invoices', label: 'Invoices', icon: FiFileText, module: 'hostel_invoices' },
        { to: '/hostel-finances', label: 'Payments', icon: FiCreditCard, module: 'hostel_finances' },
        { to: '/hostel-requests', label: 'Requests', icon: FiClipboard, module: 'hostel_requests' },
      ],
    },
    {
      label: 'Administration',
      links: [
        { to: '/roles', label: 'Roles & Permissions', icon: FiShield, module: 'roles_users' },
        { to: '/users', label: 'Admin Users', icon: FiUser, module: 'roles_users' },
        { to: '/audit-log', label: 'Audit Log', icon: FiActivity, module: 'audit_log' },
      ],
    },
  ],
};

// Hide any nav link the current admin user's role can't view. Dashboard is
// always kept (it's the safe landing page, and its own sections/cards are
// individually permission-gated); a group with no visible links is dropped.
// `module` may be a single key or an array (shown if the user can view ANY
// of them — e.g. Bulk Import covers both the student and hostel sub-modules).
function filterNavByPermission(nav, user) {
  const visible = (link) => {
    if (link.to === '/' || !link.module) return true;
    const modules = Array.isArray(link.module) ? link.module : [link.module];
    return modules.some(m => can(user, m, 'view'));
  };
  return {
    ...nav,
    groups: nav.groups
      .map(group => ({ ...group, links: group.links.filter(visible) }))
      .filter(group => group.links.length > 0),
  };
}

function AdminShell({ user, onLogout, children }) {
  const [, setSidebarOpen] = useSidebarDrawer();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const poll = () => {
      getUnreadNotificationCount().then(res => { if (!cancelled) setUnreadCount(res.data.count); }).catch(() => {});
    };
    poll();
    const interval = setInterval(poll, 45000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user]);

  if (!user) return children;
  return (
    <PermissionsProvider user={user}>
      <div className="app-shell">
        <MobileTopChrome toggleSidebar={() => setSidebarOpen(o => !o)} />
        <div className="app-sidebar-overlay" onClick={() => setSidebarOpen(false)} />
        <Sidebar
          items={filterNavByPermission(ADMIN_NAV, user)}
          identity={{ subtitle: 'Candidate Portal', name: user.full_name || user.username, sub: null }}
          onLogout={onLogout}
          closeSidebar={() => setSidebarOpen(false)}
          badges={{ '/notifications': unreadCount }}
        />
        <main className="app-main">
          <div className="app-topbar">
            <div>
              <h1>Candidate &amp; Hostel Management</h1>
              <p>Welcome back, {user.full_name || user.username}</p>
            </div>
          </div>
          <div className="container">{children}</div>
        </main>
      </div>
    </PermissionsProvider>
  );
}

function ProtectedRoute({ children, user }) {
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// ── Hostel portal shell ──────────────────────────────────────────────
const HOSTEL_NAV = {
  homePath: '/hostel/dashboard',
  groups: [
    {
      label: null,
      links: [
        { to: '/hostel/dashboard', label: 'Dashboard', icon: FiGrid },
        { to: '/hostel/admissions', label: 'Residents', icon: FiUserCheck },
        { to: '/hostel/attendance', label: 'Attendance', icon: FiCheckSquare },
        { to: '/hostel/meals', label: 'Meals', icon: FiCoffee },
        { to: '/hostel/invoices', label: 'Invoices', icon: FiFileText },
        { to: '/hostel/finances', label: 'Payments', icon: FiCreditCard },
        { to: '/hostel/requests', label: 'My Requests', icon: FiClipboard },
        { to: '/hostel/account', label: 'Account', icon: FiUser },
      ],
    },
  ],
};

function HostelShell({ hostel, onLogout, children }) {
  const [, setSidebarOpen] = useSidebarDrawer();
  if (!hostel) return children;
  return (
    <div className="app-shell">
      <MobileTopChrome toggleSidebar={() => setSidebarOpen(o => !o)} />
      <div className="app-sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      <Sidebar
        items={HOSTEL_NAV}
        identity={{ subtitle: 'Partner Portal', name: hostel.name || 'Hostel', sub: <span style={{ display: 'block', color: 'rgba(255,255,255,0.5)' }}>{hostel.incharge_name}</span> }}
        onLogout={onLogout}
        closeSidebar={() => setSidebarOpen(false)}
      />
      <main className="app-main dark">
        <div className="app-topbar">
          <div>
            <h1>Hostel Partner Portal</h1>
            <p>{new Date().toLocaleDateString('en-PK', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>
        <div className="container">{children}</div>
      </main>
    </div>
  );
}

function HostelPortalRoute({ children, hostel }) {
  if (!hostel) return <Navigate to="/hostel/login" replace />;
  return children;
}

// ── Root App ────────────────────────────────────────────────────────────
function App() {
  const [user, setUser] = useState(null);
  const [hostelUser, setHostelUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await verifyToken();
        setUser(response.data.user);
      } catch { localStorage.removeItem('token'); localStorage.removeItem('user'); }
    }
    const hostelToken = localStorage.getItem('hostelToken');
    if (hostelToken) {
      try {
        const res = await hostelVerifyToken();
        setHostelUser(res.data);
      } catch { localStorage.removeItem('hostelToken'); localStorage.removeItem('hostelUser'); }
    }
    setLoading(false);
  };

  const handleLogin = (userData) => setUser(userData);

  const handleLogout = async () => {
    try { await logout(); } catch { }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const handleHostelLogin = (hostelData) => setHostelUser(hostelData);

  const handleHostelLogout = () => {
    localStorage.removeItem('hostelToken');
    localStorage.removeItem('hostelUser');
    setHostelUser(null);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(135deg, #091530 0%, #0e1e45 55%, #152c66 100%)' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* ── Hostel Portal ── */}
        <Route path="/hostel/login" element={
          hostelUser ? <Navigate to="/hostel/dashboard" replace /> : <HostelLogin onLogin={handleHostelLogin} />
        } />

        <Route path="/hostel/*" element={
          <HostelShell hostel={hostelUser} onLogout={handleHostelLogout}>
            <Routes>
              <Route path="dashboard" element={<HostelPortalRoute hostel={hostelUser}><HostelDashboard hostel={hostelUser} /></HostelPortalRoute>} />
              <Route path="admissions" element={<HostelPortalRoute hostel={hostelUser}><HostelAdmissionsPortal hostel={hostelUser} /></HostelPortalRoute>} />
              <Route path="attendance" element={<HostelPortalRoute hostel={hostelUser}><HostelAttendancePortal hostel={hostelUser} /></HostelPortalRoute>} />
              <Route path="meals" element={<HostelPortalRoute hostel={hostelUser}><HostelMealsPortal hostel={hostelUser} /></HostelPortalRoute>} />
              <Route path="invoices" element={<HostelPortalRoute hostel={hostelUser}><HostelInvoicesPortal hostel={hostelUser} /></HostelPortalRoute>} />
              <Route path="finances" element={<HostelPortalRoute hostel={hostelUser}><HostelFinancesPortal /></HostelPortalRoute>} />
              <Route path="requests" element={<HostelPortalRoute hostel={hostelUser}><HostelRequestsPortal /></HostelPortalRoute>} />
              <Route path="account" element={<HostelPortalRoute hostel={hostelUser}><HostelAccountPortal hostel={hostelUser} /></HostelPortalRoute>} />
              <Route path="*" element={<Navigate to="/hostel/dashboard" replace />} />
            </Routes>
          </HostelShell>
        } />

        {/* ── Admin Portal ── */}
        <Route path="/*" element={
          <>
            {!user ? (
              <Routes>
                <Route path="login" element={<Login onLogin={handleLogin} />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            ) : (
              <AdminShell user={user} onLogout={handleLogout}>
                <Routes>
                  <Route path="login" element={<Navigate to="/" replace />} />
                  <Route path="/" element={<ProtectedRoute user={user}><Dashboard /></ProtectedRoute>} />
                  <Route path="notifications" element={<ProtectedRoute user={user}><Notifications /></ProtectedRoute>} />
                  <Route path="students" element={<ProtectedRoute user={user}><Students /></ProtectedRoute>} />
                  <Route path="bulk-import" element={<ProtectedRoute user={user}><BulkImport /></ProtectedRoute>} />
                  <Route path="campuses" element={<ProtectedRoute user={user}><Campuses /></ProtectedRoute>} />
                  <Route path="courses" element={<ProtectedRoute user={user}><Courses /></ProtectedRoute>} />
                  <Route path="hostels" element={<ProtectedRoute user={user}><Hostels /></ProtectedRoute>} />
                  <Route path="hostel-admissions" element={<ProtectedRoute user={user}><HostelAdmissions /></ProtectedRoute>} />
                  <Route path="hostel-attendance" element={<ProtectedRoute user={user}><Attendance /></ProtectedRoute>} />
                  <Route path="institute-attendance" element={<ProtectedRoute user={user}><InstituteAttendance /></ProtectedRoute>} />
                  <Route path="hostel-meals" element={<ProtectedRoute user={user}><Meals /></ProtectedRoute>} />
                  <Route path="hostel-billing" element={<ProtectedRoute user={user}><HostelBilling /></ProtectedRoute>} />
                  <Route path="invoices" element={<ProtectedRoute user={user}><Invoices /></ProtectedRoute>} />
                  <Route path="hostel-finances" element={<ProtectedRoute user={user}><HostelFinances /></ProtectedRoute>} />
                  <Route path="hostel-requests" element={<ProtectedRoute user={user}><HostelRequests /></ProtectedRoute>} />
                  <Route path="roles" element={<ProtectedRoute user={user}><Roles /></ProtectedRoute>} />
                  <Route path="users" element={<ProtectedRoute user={user}><Users /></ProtectedRoute>} />
                  <Route path="audit-log" element={<ProtectedRoute user={user}><AuditLog /></ProtectedRoute>} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AdminShell>
            )}
          </>
        } />
      </Routes>
    </Router>
  );
}

export default App;
